<script setup lang="ts">
  import { nextTick, onMounted, onUnmounted, onBeforeUnmount, ref } from 'vue'
  import { useAppConfig } from 'nuxt/app'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import useOutbrainAds, {
    type OutbrainElementData,
    getAdBrandFromElement,
  } from '../../composables/ad-ops/useOutbrainAds'
  import useAdFunctions from '../../composables/ad-ops/useAdFunctions'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import {
    getIndexOfPlacement,
    calculateFeedPosition,
  } from '../../utils/ad-ops/adFunctions'
  import { createExtrnId } from '../../utils/string/uuid'
  import { resolveConsent } from '../../utils/consent/consentGate'
  import debug from '../../utils/console'
  import { watchAdVisibility } from '../../utils/ad-ops/firstAdVisible'
  import {
    reportNativeAdRendered,
    reportNativeAdRequest,
  } from '../../utils/ad-ops/adRequestLifecycle'

  const logger = debug.withTag('Outbrain:DXT-1780')

  interface OutbrainWidgetAttributes {
    'data-ob-portalUrl': string
    'data-ob-language': string
    'data-widget-id': string
    'data-external-id': string
    'data-ob-installation-key': string
    'data-external-secondary-id': string
    'data-render-registered': boolean
  }

  /**
   * Props for the Outbrain component
   * @property placement - The placement identifier for the ad
   * @property widgetId - The Outbrain widget ID
   * @property tilePosition - Optional position of the tile in a list
   * @property adHeight - Optional height of the ad in pixels
   * @property adWidth - Optional width of the ad in pixels
   * @property  lazyRender - controls -visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * AND visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   * @property adInstanceId - Optional unique identifier for the ad instance
   * @property sticky - Optional sticky class for the ad
   */
  export interface OutbrainProps {
    placement: string
    widgetId: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
    isFeed?: boolean
    ayInstanceCode?: string
    adRefresh?: boolean
  }

  const props = withDefaults(defineProps<OutbrainProps>(), {
    tilePosition: -1,
    lazyRender: false,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
    isFeed: false,
    ayInstanceCode: '',
  })

  if (!props.placement || !props.widgetId) {
    throw new Error(
      `Outbrain component requires placement and widgetId props- ${props.placement}`
    )
  }

  const appConfig = useAppConfig()
  const outbrain = appConfig?.adops?.outbrain ?? {}

  const { adClicked, renderWhenCurrentElementIsVisible } = useAdFunctions()

  const { track } = useAnalytics()

  const {
    callRenderResearchWidgets,
    triggerOutbrainRenderedEvent,
    generateExternalSecondaryId,
  } = useOutbrainAds()

  const placementClassName = ref<string>(props.placement)
  const widgetWrapperEl = useTemplateRef<HTMLElement | null>('widgetWrapperEl')
  const adBrand = ref<string>('')
  const hasTrackedRender = ref<boolean>(false) // Track if we've already fired the event

  // Initialize widget attributes once on client side
  const widgetAttributes = ref<OutbrainWidgetAttributes>({
    'data-ob-portalUrl': '',
    'data-ob-language': 'en',
    'data-widget-id': props.widgetId,
    'data-external-id': '',
    'data-ob-installation-key': outbrain?.installationKey || '',
    'data-external-secondary-id': createExtrnId(),
    'data-render-registered': false,
  })

  // Set client-side attributes on mount
  if (import.meta.client) {
    widgetAttributes.value['data-ob-portalUrl'] = getTruncatedUrl()
    widgetAttributes.value['data-external-id'] = window.location.host
    widgetAttributes.value['data-external-secondary-id'] =
      generateExternalSecondaryId()
  }

  function getEventData(): NtpAdEvent {
    const eventData: NtpAdEvent = {
      ntp_tile_position: props.tilePosition,
      ntp_ad_provider: 'outbrain',
      ntp_creative_id: props.widgetId,
      ntp_placement_name: props.placement,
      ntp_ad_brand: adBrand.value,
      ntp_extern_id: widgetAttributes.value['data-external-id'],
      ntp_extern_secondary_id:
        widgetAttributes.value['data-external-secondary-id'],
    }

    if (props.ayInstanceCode) {
      eventData.ntp_ay_instance_code = props.ayInstanceCode
      eventData.ntp_ad_refresh = props.adRefresh
    }

    return eventData
  }

  function handleAdClick(event: Event) {
    const clickedAnchor = event.currentTarget

    if (!(clickedAnchor instanceof HTMLElement)) {
      return
    }

    const tileElement =
      clickedAnchor.closest('.ob-dynamic-rec-container') ||
      clickedAnchor.closest('.ob-rec-box') ||
      clickedAnchor.parentElement

    const tileBrand = tileElement
      ? getAdBrandFromElement(tileElement)
      : adBrand.value

    // Calculate decimal position for feed placements: cardIdx + (tileIndex + 1) / 10
    // For single-tile placements (no data attributes), use props.tilePosition
    const tileIndexAttr = clickedAnchor.getAttribute('data-tile-index')
    const cardIdxAttr = clickedAnchor.getAttribute('data-card-idx')

    logger.debug('[handleAdClick] Click detected', {
      placement: props.placement,
      cardIdxAttr,
      tileIndexAttr,
      propsTilePosition: props.tilePosition,
      isFeed: props.isFeed,
    })

    let tilePosition = props.tilePosition // Default to prop for single-tile placements
    if (props.isFeed) {
      const feedPosition = calculateFeedPosition(cardIdxAttr, tileIndexAttr)
      if (feedPosition !== null) {
        tilePosition = feedPosition
        logger.debug('[handleAdClick] Using FEED position', {
          placement: props.placement,
          tilePosition,
        })
      } else {
        logger.debug(
          '[handleAdClick] Feed position unavailable, falling back to prop',
          {
            placement: props.placement,
            cardIdxAttr,
            tileIndexAttr,
            tilePosition,
          }
        )
      }
    } else {
      logger.debug('[handleAdClick] Using SINGLE-TILE position', {
        placement: props.placement,
        tilePosition,
      })
    }

    const clickEventData: NtpAdEvent = {
      ...getEventData(),
      ntp_ad_brand: tileBrand || adBrand.value,
      ntp_tile_position: tilePosition,
    }

    adClicked(clickEventData, props.widgetId)
  }

  function detachClickEventFromAnchorTags(element: Element | null) {
    if (!element) return

    const registeredAnchors = element.querySelectorAll(
      'a[data-click-registered="true"]'
    )
    registeredAnchors.forEach((anchor) => {
      anchor.removeEventListener('click', handleAdClick)
      anchor.removeAttribute('data-click-registered')
    })
  }

  // Instance-specific observer state (not shared across components)
  const mutationObserver = ref<MutationObserver | null>(null)
  const fallbackInterval = ref<NodeJS.Timeout | null>(null)
  const singleTileRetryTimeout = ref<NodeJS.Timeout | null>(null)
  const instrumentedWidgets = new Set<Element>()

  function attachClickHandlersToWidget(widget: Element, cardIdx: string) {
    if (instrumentedWidgets.has(widget)) {
      return 0
    }

    logger.debug('[attachClickHandlersToWidget] FEED widget found', {
      placement: props.placement,
      cardIdx,
      widgetClass: widget.className,
    })

    const itemsContainer = widget.querySelector('.ob-widget-items-container')
    const tiles = itemsContainer?.querySelectorAll(
      '.ob-dynamic-rec-container, .ob-rec-box'
    )

    let handlersAttached = 0

    if (tiles && tiles.length > 0) {
      tiles.forEach((tile, index) => {
        const link =
          tile.querySelector('a.ob-dynamic-rec-link') || tile.querySelector('a')

        if (link && !link.getAttribute('data-click-registered')) {
          link.setAttribute('data-click-registered', 'true')
          link.setAttribute('data-tile-index', index.toString())
          link.setAttribute('data-card-idx', cardIdx)
          link.addEventListener('click', handleAdClick)
          handlersAttached++
        }
      })

      if (!adBrand.value && tiles.length > 0) {
        const firstTileBrand = getAdBrandFromElement(tiles[0])
        if (firstTileBrand) {
          adBrand.value = firstTileBrand
        }
      }

      instrumentedWidgets.add(widget)
    }

    logger.debug('[attachClickHandlersToWidget] Attached FEED handlers', {
      placement: props.placement,
      cardIdx,
      handlersAttached,
    })

    return handlersAttached
  }

  function checkForNewFMSWidgets() {
    // FMS widgets are inside Shadow DOM: .OUTBRAIN[data-card-idx] → #shadow-root → .ob-widget.FMS_*
    // Scope query to this component's wrapper to avoid affecting other placements
    if (!widgetWrapperEl.value) return 0

    const outbrainChildDivs = widgetWrapperEl.value.querySelectorAll(
      '.OUTBRAIN[data-card-idx]'
    )

    logger.debug('[checkForNewFMSWidgets] Checking for FEED cards', {
      placement: props.placement,
      cardsFound: outbrainChildDivs.length,
    })

    const fmsWidgetsWithCardIdx: Array<{ widget: Element; cardIdx: string }> =
      []
    outbrainChildDivs.forEach((div) => {
      if (!(div instanceof HTMLElement)) return

      const cardIdx = div.getAttribute('data-card-idx') || 'unknown'
      const fmsWidget =
        div.shadowRoot?.querySelector('.ob-widget[class*="FMS"]') ||
        div.querySelector('.ob-widget[class*="FMS"]')

      if (fmsWidget) {
        fmsWidgetsWithCardIdx.push({ widget: fmsWidget, cardIdx })
      }
    })

    let totalHandlersAttached = 0
    fmsWidgetsWithCardIdx.forEach(({ widget, cardIdx }) => {
      totalHandlersAttached += attachClickHandlersToWidget(widget, cardIdx)
    })

    return totalHandlersAttached
  }

  function startObservingForFMSWidgets() {
    if (
      !import.meta.client ||
      mutationObserver.value ||
      !widgetWrapperEl.value
    ) {
      return
    }

    checkForNewFMSWidgets()

    mutationObserver.value = new MutationObserver((mutations) => {
      checkForNewFMSWidgets()

      // Shadow roots populate asynchronously after the host element appears.
      // If a mutation added a new .OUTBRAIN[data-card-idx] host, schedule a
      // short delayed re-check to catch the shadow root's async population.
      const hasNewHost = mutations.some((m) =>
        Array.from(m.addedNodes).some(
          (node) =>
            node instanceof HTMLElement &&
            (node.matches?.('.OUTBRAIN[data-card-idx]') ||
              node.querySelector?.('.OUTBRAIN[data-card-idx]'))
        )
      )

      if (hasNewHost) {
        setTimeout(checkForNewFMSWidgets, 300)
      }
    })

    // Observe only within this component's wrapper, not the entire document
    mutationObserver.value.observe(widgetWrapperEl.value, {
      childList: true,
      subtree: true,
    })

    // Fallback check every 5 seconds, capped at 60s to avoid polling forever
    const FALLBACK_INTERVAL_MS = 5000
    const FALLBACK_MAX_LIFETIME_MS = 60000
    fallbackInterval.value = setInterval(() => {
      checkForNewFMSWidgets()
    }, FALLBACK_INTERVAL_MS)

    setTimeout(() => {
      if (fallbackInterval.value) {
        clearInterval(fallbackInterval.value)
        fallbackInterval.value = null
      }
    }, FALLBACK_MAX_LIFETIME_MS)
  }

  function stopObservingForFMSWidgets() {
    if (mutationObserver.value) {
      mutationObserver.value.disconnect()
      mutationObserver.value = null
    }

    if (fallbackInterval.value) {
      clearInterval(fallbackInterval.value)
      fallbackInterval.value = null
    }
  }

  function attachClickHandlersToSingleWidget(): number {
    // Fallback for single-tile/non-feed placements
    if (!widgetWrapperEl.value) return 0

    logger.debug(
      '[attachClickHandlersToSingleWidget] Looking for SINGLE widget',
      {
        placement: props.placement,
      }
    )

    const widget =
      widgetWrapperEl.value.querySelector('.ob-widget') ||
      widgetWrapperEl.value.querySelector('.OUTBRAIN')

    if (!widget) {
      logger.warn('[attachClickHandlersToSingleWidget] No widget found', {
        placement: props.placement,
      })
      return 0
    }

    const tiles = widget.querySelectorAll(
      '.ob-dynamic-rec-container, .ob-rec-box'
    )

    logger.debug('[attachClickHandlersToSingleWidget] Found tiles', {
      placement: props.placement,
      tileCount: tiles.length,
    })

    let handlersAttached = 0
    tiles.forEach((tile) => {
      const link =
        tile.querySelector('a.ob-dynamic-rec-link') || tile.querySelector('a')

      if (link && !link.getAttribute('data-click-registered')) {
        link.setAttribute('data-click-registered', 'true')
        // For single-tile placements, don't set card-idx/tile-index
        // This ensures handleAdClick uses props.tilePosition instead
        link.addEventListener('click', handleAdClick)
        handlersAttached++
      }
    })

    logger.debug(
      '[attachClickHandlersToSingleWidget] Attached SINGLE handlers',
      {
        placement: props.placement,
        handlersAttached,
        tilePosition: props.tilePosition,
      }
    )

    // Extract brand from first tile if not already set
    if (!adBrand.value && tiles.length > 0) {
      adBrand.value = getAdBrandFromElement(tiles[0])
    }

    return handlersAttached
  }

  let stopWatchingVisibility = () => {}

  async function handleOutbrainRenderedEvent(data: OutbrainElementData) {
    // For feed layouts, tiles load asynchronously when scrolled into view.
    // Use MutationObserver to detect FMS widgets being added to DOM.

    await nextTick()

    const element: Element | null = data.element
    logger.debug('[handleOutbrainRenderedEvent] Widget detected', {
      placement: props.placement,
      isFeed: props.isFeed,
      elementClasses: element?.className,
    })

    if (props.isFeed && !mutationObserver.value) {
      logger.debug('[handleOutbrainRenderedEvent] Starting FEED observer', {
        placement: props.placement,
      })
      // Feed placement: use card-based tracking with decimal positions
      startObservingForFMSWidgets()
    } else if (!props.isFeed) {
      logger.debug(
        '[handleOutbrainRenderedEvent] Attaching SINGLE-TILE handlers',
        {
          placement: props.placement,
        }
      )
      // Single-tile placement: retry attaching handlers with backoff
      // in case Outbrain hasn't hydrated the tile DOM yet
      const maxAttempts = 5
      const retryDelay = 500
      let attempt = 0

      const tryAttach = () => {
        attempt++
        const attached = attachClickHandlersToSingleWidget()
        if (attached === 0 && attempt < maxAttempts) {
          logger.debug(
            '[handleOutbrainRenderedEvent] No handlers attached yet, retrying',
            { placement: props.placement, attempt, maxAttempts }
          )
          singleTileRetryTimeout.value = setTimeout(tryAttach, retryDelay)
        }
      }

      singleTileRetryTimeout.value = setTimeout(tryAttach, retryDelay)
    }

    if (!hasTrackedRender.value) {
      hasTrackedRender.value = true

      // Extract brand from the rendered element if not already set
      if (!adBrand.value && element) {
        adBrand.value = getAdBrandFromElement(element)
      }

      const eventData = getEventData()
      track('NtpAd_Rendered', eventData)
      stopWatchingVisibility = watchAdVisibility(
        widgetWrapperEl.value,
        'outbrain',
        props.placement
      )
    }

    // Outbrain calls every subscriber of a widget id and boxes can share one, so
    // the request lifecycle counts a render only when it happened in this box.
    // A render with no element is left unmatched.
    const box = widgetWrapperEl.value
    if (
      box &&
      element &&
      (box === element || box.contains(element) || element.contains(box))
    ) {
      reportNativeAdRendered(box)
    }
  }

  onBeforeUnmount(() => {
    stopWatchingVisibility()
    stopObservingForFMSWidgets()
    if (singleTileRetryTimeout.value) {
      clearTimeout(singleTileRetryTimeout.value)
      singleTileRetryTimeout.value = null
    }
  })

  const renderOutbrainElement = ref(false)

  async function renderOutbrainAd() {
    renderOutbrainElement.value = true

    await nextTick()

    if (
      !widgetWrapperEl.value ||
      widgetWrapperEl.value?.getAttribute('data-render-registered')
    ) {
      return
    }

    const index = getIndexOfPlacement(props.placement, widgetWrapperEl.value)

    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    // Mark as registered to prevent duplicate renders
    widgetWrapperEl.value.setAttribute('data-render-registered', 'true')

    await resolveConsent()

    if (!widgetWrapperEl.value?.isConnected) return

    reportNativeAdRequest('outbrain', props.placement, widgetWrapperEl.value)
    try {
      await callRenderResearchWidgets()
      await triggerOutbrainRenderedEvent(
        props.widgetId,
        handleOutbrainRenderedEvent
      )
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'

      const errorEventData = {
        ...getEventData(),
        ntp_error_message: errorMessage,
      }

      track('NtpAd_Render_Failed', errorEventData)

      logger.error('[renderOutbrainAd] Error rendering Outbrain ad', {
        placement: props.placement,
        widgetId: props.widgetId,
        error,
      })
    }
  }

  onMounted(() => {
    renderWhenCurrentElementIsVisible(
      widgetWrapperEl.value,
      props.lazyRender,
      props.sticky,
      renderOutbrainAd
    )
  })

  onUnmounted(() => {
    if (widgetWrapperEl.value) {
      detachClickEventFromAnchorTags(widgetWrapperEl.value)
    }
  })

  // Expose methods for testing
  defineExpose({
    renderOutbrainAd,
    handleOutbrainRenderedEvent,
  })
</script>

<template>
  <div
    ref="widgetWrapperEl"
    :class="placementClassName"
    :data-ntp-extern-id="widgetAttributes['data-external-id']"
    :data-ntp-extern-secondary-id="
      widgetAttributes['data-external-secondary-id']
    "
    :data-ad-instance-id="props.adInstanceId"
  >
    <div
      v-if="renderOutbrainElement"
      class="OUTBRAIN flex items-center justify-center"
      :class="placementClassName"
      v-bind="widgetAttributes"
    />
  </div>
</template>
